import { Modal, Button } from 'antd';
import { useState, useEffect } from 'react';
import { useNavigation } from '@/common/hooks/navigation';
import { history } from 'umi';
import posthog from 'posthog-js';
import './index.scss';

const TodoPopup = () => {
  const navigation = useNavigation();
  const [isVisible, setIsVisible] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [currentSearch, setCurrentSearch] = useState(window.location.search);

  // Track path changes
  useEffect(() => {
    const unlisten = history.listen(({ pathname, search }) => {
      setCurrentPath(pathname);
      setCurrentSearch(search);
    });
    return () => unlisten();
  }, []);

  // Check for pending calendar session
  useEffect(() => {
    // If user previously opened the calendar but didn't complete booking
    const calendarOpened = localStorage.getItem('todoCalendarOpened');
    if (calendarOpened === 'true') {
      // Clear the flag
      localStorage.removeItem('todoCalendarOpened');

      // Re-show the popup after a short delay
      setTimeout(() => {
        setIsVisible(true);
        if (process.env.NODE_ENV !== 'development') {
          posthog.capture('todo_popup_reshown_after_calendar');
        }
      }, 2000);
    }
  }, []);

  useEffect(() => {
    const popupShown = localStorage.getItem('todoPopupShown');

    // Check if we're on the home page with page=2
    const isTodoPage =
      currentPath.includes('home') && currentSearch.includes('page=2');

    if (isTodoPage && !popupShown) {
      const timer = setTimeout(() => {
        setIsVisible(true);
        if (process.env.NODE_ENV !== 'development') {
          posthog.capture('todo_popup_shown', {
            path: currentPath,
            search: currentSearch,
          });
        }
      }, 10000); // Show after 10 seconds

      return () => clearTimeout(timer);
    }
  }, [currentPath, currentSearch]);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('todoPopupShown', 'true');
    if (process.env.NODE_ENV !== 'development') {
      posthog.capture('todo_popup_closed', {
        action: 'dismissed',
      });
    }
  };

  const handleSchedule = () => {
    if (process.env.NODE_ENV !== 'development') {
      posthog.capture('todo_popup_schedule_clicked');
    }

    handleClose();
    // Add Cal.com scheduling functionality here if needed
  };

  const handleDelegate = e => {
    e.preventDefault();
    e.stopPropagation();
    if (process.env.NODE_ENV !== 'development') {
      posthog.capture('todo_popup_delegate_clicked');
    }
    handleClose();
    // Use navigation.push instead of window.location.href for better mobile compatibility
    navigation.push('/home/updatePlan');
  };

  // Enhanced touch handler for Android/Samsung compatibility
  const handleDelegateTouch = e => {
    e.preventDefault();
    e.stopPropagation();
    // Add small delay to ensure touch is registered
    setTimeout(() => {
      handleDelegate(e);
    }, 50);
  };

  // Enhanced Android detection for better compatibility
  const isAndroid =
    typeof navigator !== 'undefined' &&
    (/Android/.test(navigator.userAgent) ||
      /Samsung/.test(navigator.userAgent));

  // Additional fallback for problematic browsers
  const isSamsung =
    typeof navigator !== 'undefined' && /Samsung/.test(navigator.userAgent);

  return (
    <Modal
      open={isVisible}
      footer={null}
      centered
      closable={true}
      onCancel={handleClose}
      wrapClassName="todo-popup"
      width={600}
      style={{ width: '100%', maxWidth: '600px' }}
      maskClosable={false}
      destroyOnClose={true}
      getContainer={false}
    >
      <div className="todo-popup__content">
        <h2>Besoin de savoir quelles actions prioriser ?</h2>
        <div className="todo-popup__options">
          <div className="todo-popup__option">
            <h3>Oui ✅</h3>
            <p>
              Comprenez en 10mn comment mettre en place votre stratégie
              financière avec Optivest
            </p>
            <Button
              onClick={handleSchedule}
              href="https://calendar.app.google/ybcN7XbJp6rHUGFM8" target="_blank"
              data-cal-namespace="votre-rendez-vous-gratuit-avec-notre-expert"
              data-cal-config='{"layout":"month_view"}'
              className="todo-popup__button"
            >
              Prendre RDV gratuitement
            </Button>
          </div>
          <div className="todo-popup__option todo-popup__option--highlighted">
            <h3>Je n'ai pas le temps ⏱️</h3>
            <p>
              Déléguez la mise en place de votre stratégie financière pour
              seulement <strong>549 €</strong>
            </p>
            {isAndroid ? (
              // Enhanced fallback for Android devices (including Samsung)
              <div
                className="todo-popup__button todo-popup__button--android"
                onClick={handleDelegate}
                onTouchStart={handleDelegateTouch}
                onTouchEnd={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={handleDelegate}
                role="button"
                tabIndex={0}
                style={{
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  cursor: 'pointer',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                }}
                aria-label="Mettre mon argent en action"
              >
                Mettre mon argent en action
              </div>
            ) : (
              <Button
                type="primary"
                onClick={handleDelegate}
                onTouchStart={handleDelegateTouch}
                onTouchEnd={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="todo-popup__button"
                style={{
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                }}
                aria-label="Mettre mon argent en action"
              >
                Mettre mon argent en action
              </Button>
            )}
          </div>
        </div>
        <button className="todo-popup__close" onClick={handleClose}>
          Plus tard
        </button>
      </div>
    </Modal>
  );
};

export default TodoPopup;
